import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Paper,
  Button,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { LangContext } from "../../LanguageProvider";

export default function Sanitar() {
  const { lang, translations } = useContext(LangContext);
  const [selectedDocId, setSelectedDocId] = useState("norms");

  const docItems = [
    {
      id: "norms",
      title:
        lang === "kz"
          ? "Санитариялық нормалар"
          : "Санитарные нормы",
      subtitle:
        lang === "kz"
          ? "Санитариялық-эпидемиологиялық талаптар (2026)"
          : "Санитарно-эпидемиологические требования (2026)",
      pdfPath:
        lang === "kz"
          ? "/pdf/sanitar_kz_2026.pdf"
          : "/pdf/sanitar_ru_2026.pdf",
      icon: <DescriptionIcon color="primary" />,
      year: "2026",
    },
    {
      id: "zaklu",
      title:
        lang === "kz"
          ? "Санитариялық-эпидемиологиялық қорытынды"
          : "Санитарно-эпидемиологическое заключение",
      subtitle:
        lang === "kz"
          ? "Қорытынды құжаты (2026)"
          : "Заключение (2026)",
      pdfPath: "/pdf/sanitar_zaklu_2026.pdf",
      icon: <VerifiedUserIcon color="primary" />,
      year: "2026",
    },
  ];

  const activeDoc =
    docItems.find(item => item.id === selectedDocId) || docItems[0];

  const pageTitle =
    translations["sanitar"] ||
    (lang === "kz"
      ? "Санитариялық нормалар мен қорытындылар"
      : "Санитарные нормы и заключения");

  return (
    <Box
      sx={{
        maxWidth: "1280px",
        margin: "0 auto",
        padding: { xs: 2, md: 3 },
        marginTop: "100px",
        marginBottom: "80px",
      }}>
      {/* Header section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          mb: 4,
          gap: 1.5,
        }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontSize: { xs: "22px", md: "28px" },
            fontWeight: 700,
            color: "text.primary",
          }}>
          {pageTitle}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center">
          <Chip
            label={lang === "kz" ? "2026 жыл" : "2026 год"}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Stack>
      </Box>

      {/* Main Layout: Sidebar List + PDF Viewer */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          minHeight: "820px",
        }}>
        {/* Document Sidebar */}
        <Paper
          elevation={3}
          sx={{
            width: { xs: "100%", md: "340px" },
            flexShrink: 0,
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
          }}>
          <Box
            sx={{
              p: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {lang === "kz" ? "Құжаттар тізімі" : "Список документов"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {lang === "kz"
                ? "Қарау үшін құжатты таңдаңыз"
                : "Выберите документ для просмотра"}
            </Typography>
          </Box>

          <List component="nav" sx={{ p: 1 }}>
            {docItems.map(item => {
              const isSelected = selectedDocId === item.id;
              return (
                <ListItemButton
                  key={item.id}
                  selected={isSelected}
                  onClick={() => setSelectedDocId(item.id)}
                  sx={{
                    borderRadius: "8px",
                    mb: 1,
                    p: 1.5,
                    "&.Mui-selected": {
                      backgroundColor: "action.selected",
                      borderLeft: "4px solid",
                      borderColor: "primary.main",
                      "&:hover": {
                        backgroundColor: "action.selected",
                      },
                    },
                  }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? "primary.main" : "text.primary",
                        }}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}>
                        {item.subtitle}
                      </Typography>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Divider sx={{ my: 1 }} />

          {/* Action button inside sidebar */}
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              component="a"
              href={activeDoc.pdfPath}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
              sx={{ textTransform: "none", borderRadius: "8px" }}>
              {translations["sanitar_open_tab"] ||
                (lang === "kz"
                  ? "Жаңа қойындыда ашу"
                  : "Открыть в новой вкладке")}
            </Button>
          </Box>
        </Paper>

        {/* PDF Viewer Container */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: { xs: "650px", md: "850px" },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: 2,
            bgcolor: "background.paper",
          }}>
          {/* Header of viewer */}
          <Box
            sx={{
              p: 1.5,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "action.hover",
              borderBottom: "1px solid",
              borderColor: "divider",
              gap: 1,
              flexWrap: "wrap",
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DescriptionIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {activeDoc.title}
              </Typography>
            </Box>

            <Button
              component="a"
              href={activeDoc.pdfPath}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="contained"
              startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                fontSize: "12px",
                borderRadius: "6px",
              }}>
              {translations["sanitar_open_tab"] ||
                (lang === "kz"
                  ? "Жаңа қойындыда ашу"
                  : "Открыть в новой вкладке")}
            </Button>
          </Box>

          {/* Online iframe viewer */}
          <Box sx={{ flexGrow: 1, width: "100%", height: "100%" }}>
            <iframe
              src={activeDoc.pdfPath}
              title={activeDoc.title}
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
